/*
 * ============================================================================
 * assets/lp-page.js — Landing Page System per-page interactivity (Phase 1)
 * ----------------------------------------------------------------------------
 * WHAT:  The ONE deferred JavaScript file for an LP page (ADR-3). It handles the
 *        only two interactions Phase 1 needs, plus the sticky-bar's add-to-cart
 *        proxy. The hero soft-CTA smooth-scroll is pure CSS (no JS).
 *
 *          1. Announcement strip — session dismiss (remembered via sessionStorage).
 *          2. Sticky buy-bar     — reveal on scroll past the main buy-box, via a
 *                                   CSS class that animates transform/opacity.
 *                                   NEVER display:none on the data-lp element (ADR-10).
 *          3. Sticky CTA         — proxies its click to the MAIN buy-box ATC so
 *                                   the bar shares the main buy-box's Skio add-to-cart.
 *
 * HOW:   Loaded `defer` from layout/lp.liquid, so the DOM is parsed before this
 *        runs. No framework, no dependencies. Each behavior is null-safe: a page
 *        without the announcement strip or sticky bar simply skips that block.
 *
 * WHEN TO CHANGE:  if a Phase-2 module needs page-level JS, add a clearly-divided
 *        section here rather than shipping a second file (keep it one deferred
 *        request). If a behavior grows large, split it out then.
 * ============================================================================
 */
(function () {
  'use strict';

  // Run once the DOM is ready. Deferred scripts usually run after parsing, but
  // guard anyway so load order can never break us.
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    // ----------------------------------------------------------------------
    // 1. Announcement strip — session dismiss.
    //    The message element is always in the served first-paint DOM; this only
    //    hides the chrome <aside> when the visitor dismisses it, and remembers
    //    that for the browser session (not forever).
    // ----------------------------------------------------------------------
    var STRIP_KEY = 'lp_announcement_dismissed';
    var strip = document.querySelector('[data-lp-module="announcement-strip"]');
    if (strip) {
      // Re-hide on subsequent navigations within the same session.
      try {
        if (sessionStorage.getItem(STRIP_KEY) === '1') {
          strip.classList.add('lp-announce--dismissed');
        }
      } catch (e) { /* sessionStorage blocked (private mode) — just skip persistence */ }

      var dismissBtn = strip.querySelector('[data-lp-dismiss]');
      if (dismissBtn) {
        dismissBtn.addEventListener('click', function () {
          strip.classList.add('lp-announce--dismissed');
          try { sessionStorage.setItem(STRIP_KEY, '1'); } catch (e) { /* ignore */ }
        });
      }
    }

    // ----------------------------------------------------------------------
    // 2. Sticky buy-bar — reveal after the user scrolls PAST the main buy-box.
    //    The bar is in the DOM at first paint, translated off-screen by CSS. We
    //    add .lp-sticky--revealed (transform/opacity) once the main buy-box has
    //    scrolled above the viewport, and remove it while the main buy-box is in
    //    view (no point showing a duplicate ATC next to the real one).
    // ----------------------------------------------------------------------
    var sticky = document.querySelector('[data-lp-module="sticky-buy-bar-mobile"]');
    if (sticky) {
      var targetSelector = sticky.getAttribute('data-lp-sticky-target') || '#lp-buybox-slot-main';
      var anchor = document.querySelector(targetSelector);

      if (anchor && 'IntersectionObserver' in window) {
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            // Reveal only once the main buy-box has scrolled ABOVE the viewport
            // (top < 0), i.e. the visitor has moved past it — not before reaching it.
            var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            sticky.classList.toggle('lp-sticky--revealed', scrolledPast);
          });
        }, { threshold: 0 });
        observer.observe(anchor);
      } else if (anchor) {
        // No IntersectionObserver (very old browser): reveal on a simple scroll check.
        var onScroll = function () {
          var rect = anchor.getBoundingClientRect();
          sticky.classList.toggle('lp-sticky--revealed', rect.bottom < 0);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }

      // --------------------------------------------------------------------
      // 3. Sticky CTA — proxy the click to the MAIN buy-box ATC (shared target).
      //    Skio owns the actual add-to-cart; we just trigger the real submit
      //    button. If it cannot be found, fall back to scrolling the visitor to
      //    the main buy-box so they can complete the purchase there.
      // --------------------------------------------------------------------
      var stickyCta = sticky.querySelector('[data-lp-sticky-atc]');
      if (stickyCta) {
        stickyCta.addEventListener('click', function () {
          var mainAtc = anchor ? anchor.querySelector('button[type="submit"][name="add"]') : null;
          if (mainAtc) {
            mainAtc.click();
          } else if (anchor) {
            anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    }
  });
})();
